import { IRoutingGraph } from "@multiconsult-group/multiplacer-core";
import { IPixPath, IPixPolygon } from "@multiconsult-group/multiplacer-ui";

const to2d = (pos: number[]): [number, number] => [pos[0] ?? 0, pos[1] ?? 0];

const sizeLabel = (edge: IRoutingGraph["edges"][number]): string => {
  if ("diameterM" in edge.size) return `Ø${edge.size.diameterM}m`;
  return `${edge.size.widthM}x${edge.size.heightM}m`;
};

const strokeWidthFromSize = (edge: IRoutingGraph["edges"][number]): string => {
  if ("diameterM" in edge.size) {
    return String(edge.size.diameterM * 100);
  }

  const value = Math.max(edge.size.widthM, edge.size.heightM);
  return String(value * 100);
};

/**
 * Converts a RoutingGraph (nodes + edges) into IPixPolygon objects for 2D rendering.
 *
 * Assumptions:
 * - One coordinate system in the app (no scaling, no transforms).
 * - Viewer is 2D: we render X/Y and ignore Z.
 * - Each edge is rendered as a 2-point polyline (points: [[x,y],[x,y]]).
 */
export const routingGraphToPixPolygons = (graph: IRoutingGraph): IPixPolygon[] => {
  const nodeById = new Map(graph.nodes.map((n) => [n.id, n]));

  return graph.edges.flatMap((e) => {
    const from = nodeById.get(e.from);
    const to = nodeById.get(e.to);

    if (!from || !to) {
      // Edge references missing node(s) -> skip (or throw if you prefer)
      return [];
    }

    const p1 = to2d(from.posM);
    const p2 = to2d(to.posM);

    const label = `${e.compCode} ${sizeLabel(e)}`;
    const tooltip = `${label}\nSystem: ${graph.systemCode}\nLevel: ${graph.floorName}`;

    const poly: IPixPolygon = {
      id: `routing-edge-${graph._id}-${e.id}`,
      entityId: e.id,
      points: [p1, p2],
      fillColor: undefined,
      strokeColor: "#2407dd",
      strokeWidth: strokeWidthFromSize(e),
      text: {
        values: [label],
        color: undefined,
        fontSize: undefined,
      },
      tooltip,
      isNotClickable: false,
      isMoveable: false,
      isDragSelectable: true,
    };

    return [poly];
  });
};

export const routingGraphToPixPaths = (
  graph: IRoutingGraph,
  options?: { strokeColor?: string; strokeWidthMultiplier?: number }
): IPixPath[] => {
  const nodeById = new Map(graph.nodes.map((n) => [n.id, n]));
  const strokeColor = options?.strokeColor ?? "#2407dd";
  const strokeWidthMultiplier = options?.strokeWidthMultiplier ?? 1;

  return graph.edges.flatMap((e) => {
    const from = nodeById.get(e.from);
    const to = nodeById.get(e.to);

    if (!from || !to) return [];

    const label = `${e.compCode} ${sizeLabel(e)}`;
    const level = (graph as any).floorName ?? (graph as any).levelName ?? "";

    return [
      {
        id: `routing-path-${String((graph as any)._id ?? "unsaved")}-${e.id}`,
        points: [to2d(from.posM), to2d(to.posM)],
        strokeColor,
        strokeWidth: String(Number(strokeWidthFromSize(e)) * strokeWidthMultiplier),
        tooltip: `${label}\nSystem: ${graph.systemCode}${level ? `\nLevel: ${level}` : ""}`,
      },
    ];
  });
};
